import React from 'react';
import { Card } from './ui/Card';
import { ScoreRing } from './ui/ScoreRing';
import { Shield, Lightbulb } from 'lucide-react';
interface Objection {
  text: string;
  severity: 'High' | 'Medium' | 'Low';
  handlingType: string;
  summary: string;
  score: number;
  recommendation: string;
}
const objections: Objection[] = [{
  text: 'Interest-only option unavailable for HELOC',
  severity: 'High',
  handlingType: 'Acknowledge & Redirect',
  summary: "Agent acknowledged the concern but pivoted to cash-out refinance benefits without fully addressing the client's preference.",
  score: 5,
  recommendation: 'Offer to explore interest-only HELOC products from other lenders in the network, or present a detailed comparison showing total cost of ownership between HELOC and cash-out refi.'
}, {
  text: 'Concern about closing costs',
  severity: 'Medium',
  handlingType: 'Empathize & Educate',
  summary: 'Agent explained cost structure but missed opportunity to compare with long-term savings.',
  score: 7,
  recommendation: 'Use a concrete example: show the client how $X in closing costs saves $Y monthly, breaking even in Z months.'
}, {
  text: 'Timeline pressure — needs funds within 30 days',
  severity: 'Low',
  handlingType: 'Reassure',
  summary: 'Agent confirmed typical HELOC timeline of 2–3 weeks, which satisfied the client.',
  score: 8,
  recommendation: 'Good handling — reinforce by sending a written timeline with key milestones after the call.'
}];
const severityStyles = {
  High: 'bg-red-50 text-red-700 border-red-200',
  Medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  Low: 'bg-green-50 text-green-700 border-green-200'
};
interface ObjectionHandlingCardProps {
  highlighted?: boolean;
}
export function ObjectionHandlingCard({
  highlighted
}: ObjectionHandlingCardProps) {
  return <Card delay={0.35} highlighted={highlighted} data-id="element-174">
      <div className="flex items-center justify-between mb-6" data-id="element-175">
        <h2 className="text-2xl text-navy flex items-center gap-2" data-id="element-176">
          <Shield className="text-crimson" size={24} data-id="element-177" />
          Objection Handling
        </h2>
        <ScoreRing score={6} size="sm" dynamicColor data-id="element-178" />
      </div>

      <div className="space-y-6" data-id="element-179">
        {objections.map((obj, i) => <div key={i} className="border border-warmgray rounded-lg overflow-hidden" data-id="element-180">
            {/* Objection Header */}
            <div className="p-4 bg-gray-50" data-id="element-181">
              <div className="flex items-start justify-between gap-3" data-id="element-182">
                <div className="flex-1" data-id="element-183">
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-184">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${severityStyles[obj.severity]}`} data-id="element-185">
                      {obj.severity}
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-navy/5 text-navy border border-navy/10" data-id="element-186">
                      {obj.handlingType}
                    </span>
                  </div>
                  <h4 className="font-medium text-sm text-gray-900" data-id="element-187">
                    {obj.text}
                  </h4>
                </div>
                <ScoreRing score={obj.score} size="sm" dynamicColor data-id="element-188" />
              </div>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed" data-id="element-189">
                {obj.summary}
              </p>
            </div>

            {/* Recommended Approach */}
            <div className="p-4 bg-indigo-50/60 border-t border-indigo-100" data-id="element-190">
              <div className="flex items-start gap-2" data-id="element-191">
                <Lightbulb size={16} className="text-indigo-500 mt-0.5 shrink-0" data-id="element-192" />
                <div data-id="element-193">
                  <h5 className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1" data-id="element-194">
                    Recommended Approach
                  </h5>
                  <p className="text-sm text-indigo-900/80 leading-relaxed" data-id="element-195">
                    {obj.recommendation}
                  </p>
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </Card>;
}